import React, { useEffect, useState, useCallback } from 'react';
import { api, getAccessToken } from '../api/apiClient';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/msalConfig';

// Simple forecast type definition
export type Forecast = {
  date: string;
  temperatureC: number;
  temperatureF?: number;
  summary?: string;
};

const WeatherForecast: React.FC = () => {
  const [data, setData] = useState<Forecast[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const { instance, accounts } = useMsal();

  // Load token separately so user can see it prior to request
  const loadToken = useCallback(async () => {
    try {
      setTokenError(null);
      const t = await getAccessToken();
      setToken(t);
      if (!t) {
        setTokenError('No access token available (silent acquisition returned null).');
      }
    } catch (err: any) {
      setToken(null);
      setTokenError(err?.message || 'Failed to acquire token');
    }
  }, []);

  const interactiveAcquire = useCallback(() => {
    const scopes = (loginRequest.scopes || []) as string[];
    instance
      .acquireTokenRedirect({
        scopes,
        account: instance.getActiveAccount() || accounts[0],
      })
      .catch(e => console.error('interactive acquireTokenRedirect failed', e));
  }, [instance, accounts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
  const json = await api<any[]>('/weatherforecast');
  setData(Array.isArray(json) ? json : []);
      setLastUpdated(new Date());
    } catch (e: any) {
      setError(e.message || 'Failed to load forecast');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadToken(); }, [loadToken]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: '1.5rem', maxWidth: 900, margin: '0 auto' }}>
      <h1>Weather Forecast</h1>

      <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={load} disabled={loading} style={{ padding: '.5rem 1rem', cursor: 'pointer' }}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        {lastUpdated && <small>Last updated: {lastUpdated.toLocaleTimeString()}</small>}
      </div>

      <details style={{ marginTop: '1rem' }}>
        <summary style={{ cursor: 'pointer' }}>Access Token (debug)</summary>
        {token && (
          <pre style={{ maxWidth: '100%', overflowX: 'auto', background: '#f5f5f5', padding: '.75rem', fontSize: '.65rem', lineHeight: 1.25 }}>
            {token}
          </pre>
        )}
        {!token && (
          <p style={{ fontSize: '.8rem', fontStyle: 'italic', marginTop: '.5rem' }}>
            {tokenError || 'No token available (user not signed in or scope not granted).'}
          </p>
        )}
        <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginTop: '.5rem' }}>
          <button type="button" onClick={loadToken} style={{ padding: '.25rem .75rem' }}>Refresh Token</button>
          {!token && (
            <button type="button" onClick={interactiveAcquire} style={{ padding: '.25rem .75rem' }}>
              Grant Consent / Re-auth
            </button>
          )}
        </div>
      </details>

      {error && <div style={{ marginTop: '1rem', color: '#b00020' }}>Error: {error}</div>}
      {!error && !loading && data.length === 0 && <p style={{ marginTop: '1rem' }}>No forecast data.</p>}

      <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
        {data.length > 0 && (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Temp (°C)</th>
                <th style={thStyle}>Temp (°F)</th>
                <th style={thStyle}>Summary</th>
              </tr>
            </thead>
            <tbody>
              {data.map((f, i) => {
                const fahrenheit = f.temperatureF ?? Math.round(f.temperatureC * 9 / 5 + 32);
                return (
                  <tr key={i}>
                    <td style={tdStyle}>{formatDate(f.date)}</td>
                    <td style={tdStyle}>{f.temperatureC}</td>
                    <td style={tdStyle}>{fahrenheit}</td>
                    <td style={tdStyle}>{f.summary || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = { textAlign: 'left', borderBottom: '2px solid #ccc', padding: '.5rem' };
const tdStyle: React.CSSProperties = { borderBottom: '1px solid #eee', padding: '.5rem', verticalAlign: 'top' };

function formatDate(value: string) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default WeatherForecast;
